import { Switch, Route, Redirect, useLocation } from 'react-router-dom';
import { useCallback, useMemo } from 'react';

import { WorldWrapper } from '../containers/app/worldWrapper';
import { SelectorBar } from '../layout/body/selectorBar';
import { AgreementsRoutes } from './agreements-route';
import { BatteryRoutes } from './battery-route';
import { BatteryWarrantyRoutes } from './battery-warranty-route';
import { BulkActionsRoute } from './bulk-actions-route';
import { CoreRoutes } from './core-route';
import { EnrollmentRoute } from './enrollment-route';
import { HomeLocationManagementRoute } from './home-locations-route';
import { StandardPage } from './parts/standardPage';
import { SettingsRoutes } from './settings-route';
import { UserManagementRoute } from './users-route';
import { useBaseWorldRoutes, useWorldRoutes } from './parts/allWorldRoutes';
import { useAgreementsContext } from '../context/agreements';
import { getWorlds, World } from '../services/worlds/worlds';
import { useNonWorldRequest } from '../lib/useNonWorldRequest';
import { useErrorHandler } from '@lib/useErrorHandler';
import { AppLoading } from '../components/loading/appLoading';
import { WorldsProvider } from '../context/worlds';
import { productNames } from '../containers/app/productsWrapper';
import { useProductsContext } from '../context/products';
import { ZoneManagementRoute } from './zones-route';
import { useCurrentUserContext } from '../context/currentUser';
import { useIsExternalUser } from '../components/authentication/externalUser';
import { AccessDeniedToProduct } from '../containers/access-denied/accessDeniedToProduct';

export const WorldRoutes = () => {
  const allWorldRoutes = useWorldRoutes();
  const allBaseWorldRoutes = useBaseWorldRoutes();
  const currentUser = useCurrentUserContext();
  const { isExternalUser } = useIsExternalUser(currentUser.email);
  const products = useProductsContext();

  const getPath = (pathWithoutWorldId: string) => `/:worldId${pathWithoutWorldId}`;

  const { agreementsToAccept } = useAgreementsContext();

  // If eula or dpa agreements are required, user may only access agreements pages
  const agreementRedirect = isExternalUser && agreementsToAccept.length > 0 && <Redirect to={allWorldRoutes.agreements.basePath} />;

  // Product Selection is gone: land directly on whichever shell the world can use. Core comes
  // first since it now contains the Batteries hub for battery-only worlds too; Battery Warranty
  // (Panasonic-only) is the sole remaining standalone product; a world with literally none of
  // these is the one place in the app that still shows an explicit access-denied render, rather
  // than bouncing into a shell that would just redirect straight back here (see core-route.tsx).
  const defaultRoute = products.coreV2.enabled || products.batteryEssentials.enabled
    ? <Redirect to={allWorldRoutes.core.overview} />
    : products.batteryWarranty.enabled
      ? <Redirect to={allWorldRoutes.batteryWarranty} />
      : <StandardPage><AccessDeniedToProduct /></StandardPage>;

  return (
    <>
      <SelectorBar />
      <Switch>
        <Route path={getPath(allBaseWorldRoutes.agreements.basePath)}>
          <AgreementsRoutes />
        </Route>
        {agreementRedirect}
        <Route path={getPath(allBaseWorldRoutes.batteryEssentials.battery)}>
          <BatteryRoutes />
        </Route>
        <Route path={getPath(allBaseWorldRoutes.batteryWarranty)}>
          <BatteryWarrantyRoutes />
        </Route>
        <Route path={getPath(allBaseWorldRoutes.bulkActions)}>
          <BulkActionsRoute />
        </Route>
        <Route path={getPath(allBaseWorldRoutes.core.overview)}>
          <CoreRoutes product={productNames.coreV2} />
        </Route>
        <Route path={getPath(allBaseWorldRoutes.settings.basePath)}>
          <SettingsRoutes />
        </Route>
        <Route path={getPath(allBaseWorldRoutes.userManagement)}>
          <UserManagementRoute />
        </Route>
        <Route path={getPath(allBaseWorldRoutes.homeLocationManagement)}>
          <HomeLocationManagementRoute />
        </Route>
        <Route path={getPath(allBaseWorldRoutes.zones.list)}>
          <ZoneManagementRoute />
        </Route>
        <Route path={getPath(allBaseWorldRoutes.enrollment)}>
          <EnrollmentRoute />
        </Route>
        <Route>
          {defaultRoute}
        </Route>
      </Switch>
    </>
  );
};

export const WorldIdRoute = () => {
  const { state } = useLocation<{ worlds: World[] }>();
  const fetchWorlds = useCallback(() => getWorlds(), []);
  const { error: worldsError, data: worlds, loading } = useNonWorldRequest<World[]>(fetchWorlds, { onError: false, initialLoading: true });
  useErrorHandler(worldsError);

  const value = useMemo(() => state?.worlds || worlds, [state, worlds]);

  if (loading) { return <AppLoading />; }

  return (
    <WorldWrapper>
      <WorldsProvider worlds={value} >
        <WorldRoutes/>
      </WorldsProvider>
    </WorldWrapper>
  );
};
